export const WEBHOOK_PLATFORMS = [
  { value: 'ELEMENTOR', label: 'Elementor Pro' },
  { value: 'GRAVITY_FORMS', label: 'Gravity Forms' },
  { value: 'WPFORMS', label: 'WPForms' },
  { value: 'CF7', label: 'Contact Form 7' },
  { value: 'GENERIC', label: 'Other / Generic' },
] as const;

export type WebhookPlatform = (typeof WEBHOOK_PLATFORMS)[number]['value'];

export interface PlatformSetupInfo {
  title: string;
  steps: string[];
  note?: string;
}

export const PLATFORM_SETUP: Record<string, PlatformSetupInfo> = {
  ELEMENTOR: {
    title: 'Elementor Pro — one site-wide connector (do not paste per form)',
    steps: [
      'Copy the LarkPM **Webhook URL** once (including `?secret=`). It must contain the real secret, not `secret=***`. Store it in WordPress `wp-config.php` as `LARKPM_WEBHOOK_URL` — **not** on each Form widget.',
      'Install the must-use plugin `larkpm-leads.php` from your LarkPM onboarding pack (hub: `C-LarkPM/docs/wordpress/larkpm-leads.php`). Copy it to `wp-content/mu-plugins/`. It listens to **elementor_pro/forms/new_record** and POSTs JSON (Elementor Advanced Data shape) once per public inquiry.',
      'Do **not** add Elementor’s **Webhook** action on each form. Per-widget URLs are how some forms miss the CRM and others create **duplicate** leads.',
      'Keep **Email**, **Save to Database**, **reCAPTCHA**, and the thank-you redirect as they are. The connector must not block the thank-you page.',
      'Use the same custom IDs on every inquiry form: `fname` / `lname` (or `name`), `email`, `phone`, `message`, plus hidden `utm_source` / `utm_campaign` / `utm_medium`. A lead is created when the payload has **name or phone**.',
      'Submit a test on the **published** page (not the editor preview). Then open **Settings → Website Lead Integration** and confirm a **Success** row in Recent Deliveries.',
      'Fallback for a site with a **single** form only: paste the URL into that widget’s Webhook action, JSON, then submit a test on the published page.',
    ],
    note: 'Elementor Pro is required. For multiple inquiry forms, a site-wide connector is the durable setup. Strip leftover per-form Webhook actions after the plugin is live. Do not install a Deck Creations–named plugin (`dc-larkpm-leads.php` / `DC_LARKPM_WEBHOOK_URL`) unless this company is Deck Creations.',
  },
  GRAVITY_FORMS: {
    title: 'Gravity Forms — webhook feed',
    steps: [
      'Install and activate the **Gravity Forms Webhooks Add-On** (if not already installed).',
      'In WordPress admin, open **Forms** and edit your lead capture form.',
      'Go to **Settings → Webhooks**.',
      'Click **Add New** to create a webhook feed.',
      'Paste your LarkPM **Webhook URL** into **Request URL**.',
      'Set **Request Method** to **POST** and **Request Format** to **JSON**.',
      'Under **Request Body**, choose **All Fields** so every form field is sent.',
      'Save the feed, then submit a test entry on the live form.',
    ],
  },
  WPFORMS: {
    title: 'WPForms — webhooks addon',
    steps: [
      'Install and activate the **WPForms Webhooks Addon** (requires WPForms Pro).',
      'Edit your form in WPForms.',
      'Open **Settings → Webhooks**.',
      'Enable webhooks and paste your LarkPM **Webhook URL** into **Request URL**.',
      'Set **Request Method** to **POST** and **Request Format** to **JSON**.',
      'Save the form and submit a test entry on your website.',
    ],
  },
  CF7: {
    title: 'Contact Form 7 — CF7 to Webhook plugin',
    steps: [
      'Install and activate the **CF7 to Webhook** plugin.',
      'Edit your Contact Form 7 form.',
      'Open the **Webhook** tab on the form.',
      'Paste your LarkPM **Webhook URL** into the URL field.',
      'Set the request method to **POST**.',
      'Save the form and submit a test message from the live page.',
    ],
  },
  GENERIC: {
    title: 'Any webhook-capable form or automation',
    steps: [
      'Configure your form plugin, Zapier, Make, or custom code to send an **HTTP POST** on submit.',
      'Set the target URL to your LarkPM **Webhook URL** (include the full `?secret=` query string).',
      'Set **Content-Type** to `application/json`.',
      'Send form fields as a flat JSON object (field label or name → value).',
      'Include at least **name or phone** (or equivalent labels LarkPM can map).',
      'Optional: include hidden fields for **utm_campaign**, landing page URL, or referrer if your stack supports them.',
      'Submit a test entry and confirm a lead appears in LarkPM.',
    ],
    note: 'Works with any tool that can POST JSON — Zapier, Make (Integromat), custom WordPress plugins, etc.',
  },
};

export function getPlatformSetup(platform?: string): PlatformSetupInfo {
  return PLATFORM_SETUP[platform || 'GENERIC'] || PLATFORM_SETUP.GENERIC;
}
